import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  type IncubatorMapSlot,
  type Label,
  useDuplicateIvcSlotMutation,
  useGetIncubatorMapsQuery,
  useGetLabelsQuery,
} from '@/app/services/fertWork';
import { addToast } from '@/app/slices/toast';
import { PageStatusBanner } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFertWorkSOFLabelsReport } from '@/services/reports';
import { EMBRYO_CATEGORY_COLOR_KEY_ITEMS } from '@/utils/categoryColors';

import { LabelCard } from '../LabelCard';

export type GenerateSOFLabelsProps = {
  sessionId?: string;
  className?: string;
};

interface SOFLabelEntry {
  slot: IncubatorMapSlot;
  label: Label;
  sofId: string; // e.g. "IVC-A1"
  incubatorLetter: string;
  numberInIncubator: number;
}

interface IncubatorGroup {
  incubatorName: string;
  letter: string;
  entries: SOFLabelEntry[];
}

export const GenerateSOFLabels = ({
  sessionId: sessionIdProp,
  className = '',
}: GenerateSOFLabelsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = sessionIdProp || searchParams.get('sessionId');
  const isEmbedded = !!sessionIdProp;

  const massProductionEnabled = useFeatureFlag('fert_work_mass_production');
  const [duplicateIvcSlot, { isLoading: isDuplicating }] =
    useDuplicateIvcSlotMutation();

  const handleDuplicate = async (slotId: string) => {
    if (!sessionId) return;
    try {
      await duplicateIvcSlot({ sessionId, slotId }).unwrap();
    } catch {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('fertWork.duplicate', 'Duplicate'),
          detail: t(
            'fertWork.failedToLoadRuns',
            'Failed to load runs. Please try again.',
          ),
        }),
      );
    }
  };
  const matingsPerPlate = parseInt(
    localStorage.getItem('fertwork-matings-per-plate') || '2',
  );

  const [showFab, setShowFab] = useState(false);
  const actionsRowRef = useRef<HTMLDivElement>(null);

  const {
    data: mapData,
    isLoading,
    error,
  } = useGetIncubatorMapsQuery(sessionId || '', {
    skip: !sessionId,
  });
  const { data: labelsData } = useGetLabelsQuery(sessionId || '', {
    skip: !sessionId,
  });
  const isCommercial = mapData?.sessionType === 1;
  const [generateSOFPdf, { isPending: isDownloading }] =
    useFertWorkSOFLabelsReport();

  const groups: IncubatorGroup[] = useMemo(() => {
    if (!mapData?.slots) return [];
    const labelsById = new Map(
      (labelsData ?? []).map((label) => [label.id.toLowerCase(), label]),
    );

    const occupiedSlots = mapData.slots
      .filter((s) => s.isOccupied && (s.label || s.labelId))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const incubatorMap = new Map<string, IncubatorMapSlot[]>();
    for (const slot of occupiedSlots) {
      const name = slot.incubatorName || slot.incubatorType;
      if (!incubatorMap.has(name)) {
        incubatorMap.set(name, []);
      }
      incubatorMap.get(name)!.push(slot);
    }

    const buildLabelFromSlot = (slot: IncubatorMapSlot): Label => ({
      id: slot.labelId || slot.id,
      sessionId: slot.sessionId,
      runId: '',
      runNumber: 0,
      labelNumber: 0,
      labelId: slot.labelId || slot.id,
      isHalfPlate: false,
      planCategory: slot.planCategory || '',
      aspirationType: '',
      displayOrder: slot.displayOrder,
      isDay5: slot.isDay5,
      top: slot.donorName
        ? {
            detailId: slot.id,
            donorName: slot.donorName,
            donorBreed: slot.donorBreed || '',
            sireName: slot.sireName || '',
            sireBreed: '',
            semenType: '',
            shipLabel: slot.shipLabel || '',
            oocytes: 0,
          }
        : undefined,
    });

    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let letterIdx = 0;

    return Array.from(incubatorMap.entries()).map(([incubatorName, slots]) => {
      const letter = letters[letterIdx] || String(letterIdx + 1);
      letterIdx++;

      const entries: SOFLabelEntry[] = slots.map((slot, idx) => ({
        slot,
        label:
          slot.label ||
          (slot.labelId
            ? labelsById.get(slot.labelId.toLowerCase())
            : undefined) ||
          buildLabelFromSlot(slot),
        sofId: `IVC-${letter}${idx + 1}`,
        incubatorLetter: letter,
        numberInIncubator: idx + 1,
      }));

      return { incubatorName, letter, entries };
    });
  }, [mapData, labelsData]);

  useEffect(() => {
    const el = actionsRowRef.current;
    if (!el) {
      setShowFab(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [groups.length > 0]);

  const handleBack = () => {
    navigate(-1);
  };

  const handlePrintSOF = async (incubatorName?: string) => {
    if (!sessionId) return;

    try {
      await generateSOFPdf({
        sessionId,
        incubatorName,
        ...(matingsPerPlate === 4 && { matingsPerPlate: 4 }),
      });
    } catch (err: any) {
      console.error('Failed to print IVC PDF:', err);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail: t(
            'fertWork.toasts.failedToPrintIvcLabels',
            'Failed to print IVC labels',
          ),
        }),
      );
    }
  };

  const handlePrintAll = () => handlePrintSOF();
  const handlePrintGroup = (incubatorName: string) =>
    handlePrintSOF(incubatorName);

  if (!sessionId) {
    return (
      <div className="flex flex-auto items-center justify-center">
        <div className="text-center">
          <i className="pi pi-exclamation-triangle mb-3 text-4xl text-orange-500" />
          <h2 className="mb-2 text-xl font-semibold">
            {t('fertWork.noSessionId', 'No Session ID')}
          </h2>
          <p className="text-gray-600">
            {t(
              'fertWork.selectSessionForIvc',
              'Please select a session to view IVC labels.',
            )}
          </p>
        </div>
      </div>
    );
  }

  const standaloneHeader = !isEmbedded && sessionId && (
    <div className="mb-2 flex items-center gap-2">
      <Button variant="ghost" size="icon-sm" onClick={handleBack}>
        <i className="pi pi-chevron-left" />
        <span className="sr-only">{t('common.back', 'Back')}</span>
      </Button>
      <h1 className="text-lg font-semibold sm:text-2xl">
        <span className="hidden sm:inline">
          {t(
            'fertWork.generatedIvcPlateLabels',
            'Generated IVC Plate Labels - ',
          )}
        </span>
        <span className="sm:hidden">
          {t('fertWork.ivcLabelsShort', 'IVC Labels - ')}
        </span>
        <span title={sessionId}>{sessionId.slice(0, 8).toUpperCase()}</span>
      </h1>
    </div>
  );

  if (isLoading) {
    return (
      <div
        className={`generate-sof-labels-page relative flex flex-auto flex-col ${className}`}
      >
        <div className={`flex flex-col gap-4 ${isEmbedded ? '' : 'p-4'}`}>
          {standaloneHeader}
          <PageStatusBanner
            type="loading"
            message={t('fertWork.loadingIvcLabels', 'Loading IVC labels...')}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`generate-sof-labels-page relative flex flex-auto flex-col ${className}`}
      >
        <div className={`flex flex-col gap-4 ${isEmbedded ? '' : 'p-4'}`}>
          {standaloneHeader}
          <PageStatusBanner
            type="error"
            message={t(
              'fertWork.failedToLoadMaps',
              'Failed to load incubator maps. Please try again.',
            )}
          />
        </div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div
        className={`generate-sof-labels-page relative flex flex-auto flex-col ${className}`}
      >
        <div className={`flex flex-col gap-4 ${isEmbedded ? '' : 'p-4'}`}>
          {standaloneHeader}
          <PageStatusBanner
            type="empty"
            message={t(
              'fertWork.noIncubatorMapsFound',
              'No incubator maps found. Generate incubator maps first to create IVC labels.',
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`generate-sof-labels-page relative flex flex-auto flex-col ${className}`}
    >
      <div className={`flex flex-col gap-4 ${isEmbedded ? '' : 'p-4'}`}>
        {standaloneHeader}

        <div
          data-tour="sof-actions"
          ref={actionsRowRef}
          className="flex flex-wrap gap-2"
        >
          <Button
            onClick={handlePrintAll}
            disabled={isDownloading}
            className="text-sm"
          >
            <i
              className={
                isDownloading ? 'pi pi-spin pi-spinner' : 'pi pi-print'
              }
              data-icon="inline-start"
            />
            {t('fertWork.printAll', 'Print All')}
          </Button>
        </div>

        <div
          data-tour="sof-color-key"
          className="rounded-lg border border-gray-200 bg-white p-3"
        >
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-600">
              {t('fertWork.colorKey', 'Color Key:')}
            </span>
            {EMBRYO_CATEGORY_COLOR_KEY_ITEMS.map((item) => (
              <div key={item.key} className="flex items-center gap-1.5">
                <div
                  className={`${item.key === 'halfPlateDiffSires' ? 'h-3' : 'h-4'} w-6 rounded border ${item.colorClass}`}
                ></div>
                <span className="text-sm">{t(item.translationKey)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {groups.map((group, groupIndex) => (
            <div
              key={group.letter}
              data-tour={groupIndex === 0 ? 'sof-incubator-group' : undefined}
            >
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-700 text-sm font-bold text-white">
                  {group.letter}
                </span>
                <h2 className="text-lg font-semibold text-gray-800">
                  {group.incubatorName}
                </h2>
                <span className="text-sm text-gray-500">
                  {t('fertWork.labelsCountText', '({count} labels)', {
                    count: group.entries.length,
                  })}
                </span>
                <button
                  type="button"
                  className="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
                  onClick={() => handlePrintGroup(group.incubatorName)}
                  title={t('fertWork.printGroupTitle', 'Print {name}', {
                    name: group.incubatorName,
                  })}
                  data-tour={groupIndex === 0 ? 'sof-print-group' : undefined}
                >
                  <i className="pi pi-print text-sm" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {matingsPerPlate === 4
                  ? group.entries
                      .filter((_, i) => i % 2 === 0)
                      .map((entry, i) => {
                        const secondEntry = group.entries[i * 2 + 1];
                        return (
                          <LabelCard
                            key={entry.slot.id}
                            label={entry.label}
                            secondLabel={secondEntry?.label}
                            displayLabelId={entry.sofId}
                            stripeText="IVC"
                            sessionId={sessionId}
                            readOnly
                            fourMating
                            isCommercial={isCommercial}
                          />
                        );
                      })
                  : group.entries.map((entry) => (
                      <div key={entry.slot.id} className="relative">
                        <LabelCard
                          label={entry.label}
                          displayLabelId={entry.sofId}
                          stripeText="IVC"
                          sessionId={sessionId}
                          isDuplicate={entry.slot.isDuplicate}
                          readOnly
                          isCommercial={isCommercial}
                        />
                        {massProductionEnabled &&
                          entry.slot.isMassProduction &&
                          !entry.slot.isDuplicate && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="!absolute top-1 left-1 !h-7 !w-7 rounded-full"
                                    onClick={() =>
                                      handleDuplicate(entry.slot.id)
                                    }
                                    disabled={isDuplicating}
                                  >
                                    <i
                                      className={
                                        isDuplicating
                                          ? 'pi pi-spin pi-spinner'
                                          : 'pi pi-copy'
                                      }
                                    />
                                    <span className="sr-only">
                                      {t('fertWork.duplicate', 'Duplicate')}
                                    </span>
                                  </Button>
                                }
                              />
                              <TooltipContent side="right">
                                {t('fertWork.duplicate', 'Duplicate')}
                              </TooltipContent>
                            </Tooltip>
                          )}
                      </div>
                    ))}
              </div>
            </div>
          ))}
        </div>

        {showFab && groups.length > 0 && (
          <div className="fixed right-4 bottom-4 z-50 sm:right-6 sm:bottom-6">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="icon"
                    className="!h-12 !w-12 rounded-full shadow-lg sm:!h-14 sm:!w-14"
                    onClick={handlePrintAll}
                    disabled={isDownloading}
                  >
                    <i
                      className={
                        isDownloading ? 'pi pi-spin pi-spinner' : 'pi pi-print'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.printAll', 'Print All')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.printAll', 'Print All')}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
